import { useIAMAuth } from "@/auth/iam";

/**
 * Tenant scoping for the authoring surfaces.
 *
 * The same screens serve two audiences:
 *
 * - **Medicus staff** (`Admin`, `ContentEditor`) legitimately browse *any*
 *   tenant, so the organization is an input they type. A blank value means
 *   "the base corpus", which for staff is a real and useful view.
 *
 * - **A tenant's own `ClientAdmin`** must only ever see their own organization.
 *   Leaving the org blank for them is not merely useless, it is *misleading* —
 *   `GET /rules/{region}` with no org returns Medicus's base rules, so the
 *   client's own org-layer rules would silently be absent from a screen that
 *   claims to list "rules". And typing another tenant's id just earns a 403
 *   from `TenantScopeMiddleware`. So for a ClientAdmin the org is **pinned** to
 *   their `organization_unit_id` claim and is not editable.
 *
 * This is a UX correctness concern, not a security boundary: the backend
 * derives the tenant from the same claim and rejects mismatches regardless of
 * what this app sends.
 */

export interface TenantScope {
  /** The organization to send with authoring calls; `""` means "base corpus". */
  org: string;
  /** True when the org is fixed by the caller's token and must not be edited. */
  pinned: boolean;
}

/**
 * Resolve the tenant for the current principal.
 *
 * `fallback` is the org a *staff* user has chosen (URL param, form state). It is
 * ignored entirely when the caller is tenant-scoped — there is no code path in
 * which a ClientAdmin's typed value reaches the API.
 */
export function useTenantScope(fallback = ""): TenantScope {
  const { organizationId } = useIAMAuth();
  if (organizationId !== null && organizationId !== "") {
    return { org: organizationId, pinned: true };
  }
  return { org: fallback, pinned: false };
}
